import React, { useState, useEffect, useRef } from "react";
import 'bootstrap/dist/css/bootstrap.min.css';
import '../../Styles/global-imports.scss';
import DOMPurify from 'dompurify';
import { marked } from 'marked';
import { v4 as uuidv4 } from 'uuid';

import ToggleButton from "./togglebutton";
import ChatWindow from "./ChatWindow";

function ChatBot({ email, guid }) {
    const [chatOpen, setChatOpen] = useState(false);
    const [minimized, setMinimized] = useState(false);
    const [fullscreen, setFullscreen] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const [conversation, setConversation] = useState(() => {
        const savedConversation = localStorage.getItem('copia_chat_history');
        return savedConversation ? JSON.parse(savedConversation) : [];
    });
    const [input, setInput] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [isStreaming, setIsStreaming] = useState(false);
    const [showWelcome, setShowWelcome] = useState(true);
    const [lastActivityTime, setLastActivityTime] = useState(Date.now());
    const [idlePromptShown, setIdlePromptShown] = useState(false);
    const [retryCount, setRetryCount] = useState(0);
    const [suggestedResponses, setSuggestedResponses] = useState([]);
    const [unreadMessages, setUnreadMessages] = useState(0);

    const [token, setToken] = useState(() => localStorage.getItem('copia_auth_token') || "");
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [sessionId, setSessionId] = useState(() => localStorage.getItem('copia_session_id') || "");
    const [authError, setAuthError] = useState("");
    const [streamingMessageIndex, setStreamingMessageIndex] = useState(-1);
    const [mcpLoadingState, setMcpLoadingState] = useState(null);
    const streamAbortController = useRef(null);
    const mcpLoadingInterval = useRef(null);
    const isDownloading = useRef(false);

    const mcpLoadingMessages = [
        "Looking into your query...",
        "Gathering information...",
        "Analysing data...",
        "Processing request...",
        "Almost ready..."
    ];

    const startMcpLoading = () => {
        let messageIndex = 0;
        setMcpLoadingState(mcpLoadingMessages[0]);

        mcpLoadingInterval.current = setInterval(() => {
            messageIndex = (messageIndex + 1) % mcpLoadingMessages.length;
            setMcpLoadingState(mcpLoadingMessages[messageIndex]);
        }, 4000);
    };

    const stopMcpLoading = () => {
        if (mcpLoadingInterval.current) {
            clearInterval(mcpLoadingInterval.current);
            mcpLoadingInterval.current = null;
        }
        setMcpLoadingState(null);
    };
    const idleTimerRef = useRef(null);
    const messageEndRef = useRef(null);

    useEffect(() => {
        const checkMobile = () => {
            const isMobileDevice = window.innerWidth <= 768 ||
                /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
            setIsMobile(isMobileDevice);

            if (isMobileDevice && chatOpen && !minimized) {
                setFullscreen(true);
            }
        };

        checkMobile();
        window.addEventListener('resize', checkMobile);

        return () => window.removeEventListener('resize', checkMobile);
    }, [chatOpen, minimized]);

    useEffect(() => {
        const handleOrientationChange = () => {
            setTimeout(() => {
                if (isMobile && chatOpen && !minimized) {
                    setFullscreen(true);
                }
            }, 100);
        };

        window.addEventListener('orientationchange', handleOrientationChange);
        return () => window.removeEventListener('orientationchange', handleOrientationChange);
    }, [isMobile, chatOpen, minimized]);

    useEffect(() => {
        if ("Notification" in window && Notification.permission === "default") {
            Notification.requestPermission();
        }
        const clearOldData = () => {
            const keys = Object.keys(localStorage);
            keys.forEach(key => {
                if (key.startsWith('copia_') && key !== 'copia_auth_token' && key !== 'copia_session_id') {
                    const item = localStorage.getItem(key);
                    if (item && item.length > 50000) {
                        localStorage.removeItem(key);
                    }
                }
            });
        };

        clearOldData();
    }, []);

    useEffect(() => {
        const handleBeforeUnload = () => {
            // Skip cleanup during file downloads to avoid losing the auth token mid-transfer
            if (isDownloading.current) {
                return;
            }

            if (isAuthenticated) {
                localStorage.removeItem('copia_auth_token');
                localStorage.removeItem('copia_session_id');
            }
            if (streamAbortController.current) {
                streamAbortController.current.abort();
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
            if (streamAbortController.current) {
                streamAbortController.current.abort();
            }
            if (mcpLoadingInterval.current) {
                clearInterval(mcpLoadingInterval.current);
            }
        };
    }, [isAuthenticated, token, sessionId]);

    const toggleChat = () => {
        if (chatOpen && minimized) {
            setMinimized(false);
            setUnreadMessages(0);

            if (isMobile) {
                setFullscreen(true);
            }
        } else {
            setChatOpen(!chatOpen);
            if (!chatOpen) {
                setMinimized(false);
                setUnreadMessages(0);

                if (isMobile) {
                    setFullscreen(true);
                }
            } else {
                setFullscreen(false);
            }
        }
        setLastActivityTime(Date.now());
    };

    const toggleMinimize = () => {
        setMinimized(!minimized);
        if (!minimized) {
            setUnreadMessages(0);
            setFullscreen(false);
        } else if (isMobile) {
            setFullscreen(true);
        }
    };

    const toggleFullscreen = () => {
        setFullscreen(!fullscreen);
        if (!fullscreen) {
            setMinimized(false);
        }
    };

    useEffect(() => {
        const trimmedConversation = conversation.map(msg => ({
            ...msg,
            tableData: undefined
        }));

        const MAX_STORED_MESSAGES = 20;
        const recentMessages = trimmedConversation.slice(-MAX_STORED_MESSAGES);

        try {
            localStorage.setItem('copia_chat_history', JSON.stringify(recentMessages));
        } catch (error) {
            console.warn('localStorage quota exceeded, clearing old data');
            localStorage.removeItem('copia_chat_history');
            const minimalMessages = recentMessages.slice(-10);
            localStorage.setItem('copia_chat_history', JSON.stringify(minimalMessages));
        }
    }, [conversation]);

    useEffect(() => {
        if (messageEndRef.current) {
            messageEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [conversation]);

    useEffect(() => {
        if (!chatOpen || minimized || !isAuthenticated) return;

        idleTimerRef.current = setInterval(() => {
            const idleTimeMs = Date.now() - lastActivityTime;
            if (idleTimeMs > 120000 && !idlePromptShown && conversation.length > 0) {
                setIdlePromptShown(true);
                setConversation(prev => [...prev, {
                    sender: "Copia",
                    text: "Are you still there? Is there anything else I can help you with?",
                    timestamp: new Date().toISOString(),
                    read: false
                }]);
                setUnreadMessages(prev => prev + 1);
            }

            if (idleTimeMs > 300000 && !minimized && !isMobile) {
                setMinimized(true);
                setFullscreen(false);
            }
        }, 30000);

        return () => clearInterval(idleTimerRef.current);
    }, [chatOpen, minimized, lastActivityTime, idlePromptShown, conversation.length, isAuthenticated, isMobile]);

    const formatTimestamp = (isoString) => {
        const date = new Date(isoString);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.round(diffMs / 60000);

        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins}m ago`;

        const hours = date.getHours();
        const minutes = date.getMinutes();
        return `${hours % 12 || 12}:${minutes.toString().padStart(2, '0')} ${hours >= 12 ? 'PM' : 'AM'}`;
    };


    const renderMarkdown = (text) => {
        const cleanText = text.replace(/▋$/, '');
        const rawHtml = marked(cleanText);
        return { __html: DOMPurify.sanitize(rawHtml) };
    };

    const fetchGreeting = async (bearerToken) => {
        try {
            const response = await fetch("https://copiaai.c247.tools/api/chat/greeting", {
                method: "GET",
                headers: {
                    "Authorization": `Bearer ${bearerToken}`
                }
            });

            if (response.ok) {
                const data = await response.json();
                return data.message;
            }
        } catch (error) {
            console.error("Failed to fetch greeting:", error);
        }

        return "Hi, I am Copia247. How may I help you today?";
    };

    const fetchSuggestions = async () => {
        try {
            const response = await fetch(`https://copiaai.c247.tools/api/chat/suggestions?sessionId=${sessionId}`, {
                method: "GET",
                headers: {
                    "Authorization": `Bearer ${token}`
                }
            });

            if (response.ok) {
                const suggestions = await response.json();
                return Array.isArray(suggestions) ? suggestions : [];
            }
        } catch (error) {
            console.error("Failed to fetch suggestions:", error);
        }

        return [];
    };
    const authenticateUser = async () => {
        setIsLoading(true);
        setAuthError("");

        try {
            if (!email || !guid) {
                throw new Error("Authentication credentials are missing");
            }

            const application = "MANAGERREPORT";

            const response = await fetch("https://copiaai.c247.tools/api/auth/login", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email, guid, application })
            });

            if (!response.ok) {
                const errorData = await response.json();
                console.error("Authentication error:", errorData);
                setIsAuthenticated(false);
                throw new Error("Authentication failed");
            }

            const data = await response.json();

            setToken(data.token);
            localStorage.setItem('copia_auth_token', data.token);

            const newSessionId = uuidv4();
            setSessionId(newSessionId);
            localStorage.setItem('copia_session_id', newSessionId);

            setIsAuthenticated(true);
            setShowWelcome(false);

            setConversation([
                {
                    sender: "Copia",
                    text: await fetchGreeting(data.token),
                    timestamp: new Date().toISOString(),
                    read: true
                }
            ]);

        } catch (error) {
            console.error("Authentication error:", error);
            setAuthError("Failed to authenticate. Please try again.");
            setIsAuthenticated(false);
        } finally {
            setIsLoading(false);
        }
    };

    const verifyToken = async (tokenToVerify) => {
        try {
            const response = await fetch("https://copiaai.c247.tools/api/auth/verify", {
                method: "GET",
                headers: {
                    "Authorization": `Bearer ${tokenToVerify}`
                }
            });

            if (!response.ok) {
                throw new Error("Token verification failed");
            }

            const data = await response.json();

            if (data.isValid) {
                setIsAuthenticated(true);

                if (!sessionId) {
                    const newSessionId = uuidv4();
                    setSessionId(newSessionId);
                    localStorage.setItem('copia_session_id', newSessionId);
                }
            } else {
                throw new Error("Invalid token");
            }
        } catch (error) {
            console.error("Token verification error:", error);
            setIsAuthenticated(false);
            setToken("");
            localStorage.removeItem('copia_auth_token');
            localStorage.removeItem('copia_session_id');
        }
    };

    const startChat = () => {
        authenticateUser();
    };

    const clearChat = () => {
        if (window.confirm("Are you sure you want to clear your chat history?")) {
            localStorage.removeItem('copia_chat_history');

            const newSessionId = uuidv4();
            setSessionId(newSessionId);
            localStorage.setItem('copia_session_id', newSessionId);

            setConversation([
                {
                    sender: "Copia",
                    text: "Hi, I am Copia247. How may I help you today?",
                    timestamp: new Date().toISOString(),
                    read: true,
                    suggestions: ["Tell me about Copia247", "How do I use this system?", "What can you help me with?"]
                }
            ]);
        }
    };

    const handleSuggestedResponse = (text) => {
        sendMessage(text);
        setSuggestedResponses([]);
    };

    const updateActivity = () => {
        setLastActivityTime(Date.now());
        setIdlePromptShown(false);
    };

    const sendMessage = async (messageText) => {
        setRetryCount(0);

        const userMessage = {
            sender: "You",
            text: messageText,
            timestamp: new Date().toISOString(),
            read: true
        };
        setConversation(prev => [...prev, userMessage]);
        setInput("");
        updateActivity();

        setIsLoading(true);

        await fetchBotResponseStream(messageText);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!input.trim()) return;
        sendMessage(input);
    };

    const fetchBotResponseStream = async (question, isRetry = false) => {
        try {
            if (!isAuthenticated || !token) {
                throw new Error("You need to be authenticated to send messages");
            }

            if (streamAbortController.current) {
                streamAbortController.current.abort();
            }

            streamAbortController.current = new AbortController();

            const response = await fetch("https://copiaai.c247.tools/api/chat/stream", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify({
                    sessionId: sessionId,
                    userMessage: question,
                    email: email
                }),
                signal: streamAbortController.current.signal
            });

            setIsLoading(false);

            if (response.status === 401) {
                setIsAuthenticated(false);
                setToken("");
                localStorage.removeItem('copia_auth_token');
                throw new Error("Your session has expired. Please refresh the page to authenticate again.");
            }

            if (!response.ok) {
                throw new Error(await response.text());
            }

            // +1 because the user message was already appended above
            const streamingMessageIndex = conversation.length + 1;
            setStreamingMessageIndex(streamingMessageIndex);
            setIsStreaming(true);

            const initialBotMessage = {
                sender: "Copia",
                text: "",
                timestamp: new Date().toISOString(),
                read: !minimized && chatOpen,
                isStreaming: true,
                format: "markdown"
            };

            setConversation(prev => [...prev, initialBotMessage]);

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let accumulatedText = "";

            try {
                while (true) {
                    const { done, value } = await reader.read();

                    if (done) break;

                    const chunk = decoder.decode(value, { stream: true });

                    if ((chunk.includes('[{mcp_call') && !chunk.includes('[{mcp_call_complete')) || chunk.includes('[{mcp_list_tools}]')) {
                        startMcpLoading();
                    }

                    if (chunk.includes('[{mcp_call_complete') || chunk.includes('[{mcp_call_failed}]')) {
                        stopMcpLoading();
                    }

                    accumulatedText += chunk;

                    setConversation(prev =>
                        prev.map((msg, index) =>
                            index === streamingMessageIndex
                                ? { ...msg, text: accumulatedText, isStreaming: true }
                                : msg
                        )
                    );

                    if ((minimized || !chatOpen) && accumulatedText.trim() &&
                        !initialBotMessage.notifiedUnread) {
                        setUnreadMessages(prev => prev + 1);
                        initialBotMessage.notifiedUnread = true;

                        if ("Notification" in window && Notification.permission === "granted") {
                            new Notification("New message from Copia247", {
                                body: accumulatedText.substring(0, 50) + "..."
                            });
                        }
                    }
                }
            } finally {
                reader.releaseLock();
                stopMcpLoading();
            }

            setConversation(prev =>
                prev.map((msg, index) =>
                    index === streamingMessageIndex
                        ? { ...msg, text: accumulatedText, isStreaming: false }
                        : msg
                )
            );

            setIsStreaming(false);
            setStreamingMessageIndex(-1);
            stopMcpLoading();
            setRetryCount(0);

            const suggestions = await fetchSuggestions();
            setSuggestedResponses(suggestions);

        } catch (error) {
            setIsLoading(false);
            setIsStreaming(false);
            setStreamingMessageIndex(-1);
            stopMcpLoading();

            if (error.name === 'AbortError') {
                console.log('Stream request was aborted');
                return;
            }

            console.error("Error fetching response:", error);

            if (error.message.includes("session has expired")) {
                setShowWelcome(true);
                return;
            }

            let errorMessage = "We're experiencing connection issues. Please check your internet connection and try again.";

            try {
                const errorData = JSON.parse(error.message);
                if (errorData.message) {
                    errorMessage = errorData.message;
                }
            } catch {
                errorMessage = error.message || errorMessage;
            }

            if (retryCount < 2 && !isRetry && !errorMessage.includes("Security Notice")) {
                setRetryCount(prev => prev + 1);
                setTimeout(() => {
                    setConversation(prev => [...prev, {
                        sender: "System",
                        text: "Connection issue detected. Retrying...",
                        timestamp: new Date().toISOString(),
                        isSystem: true,
                        read: true
                    }]);
                    fetchBotResponseStream(question, true);
                }, 1500);
                return;
            }

            setConversation(prev => [...prev, {
                sender: "Copia",
                text: errorMessage,
                timestamp: new Date().toISOString(),
                read: !minimized && chatOpen,
                isError: true
            }]);

            if (minimized || !chatOpen) {
                setUnreadMessages(prev => prev + 1);
            }
        }
    };

    const markMessagesAsRead = () => {
        setConversation(prev =>
            prev.map(msg => ({ ...msg, read: true }))
        );
        setUnreadMessages(0);
    };

    const summariseConversation = () => {
        const messageCount = conversation.length;
        const userMessageCount = conversation.filter(msg => msg.sender === "You").length;
        const botMessageCount = conversation.filter(msg => msg.sender === "Copia").length;

        const currentDate = new Date();
        const fileDate = `${currentDate.getFullYear()}-${(currentDate.getMonth() + 1).toString().padStart(2, '0')}-${currentDate.getDate().toString().padStart(2, '0')}`;
        const fileTime = `${currentDate.getHours().toString().padStart(2, '0')}-${currentDate.getMinutes().toString().padStart(2, '0')}`;

        let transcriptContent = `# Copia247 Chat Transcript\n`;
        transcriptContent += `Date: ${currentDate.toLocaleDateString()}\n`;
        transcriptContent += `Time: ${currentDate.toLocaleTimeString()}\n\n`;
        transcriptContent += `## Conversation Summary\n`;
        transcriptContent += `- Total Messages: ${messageCount}\n`;
        transcriptContent += `- Your Messages: ${userMessageCount}\n`;
        transcriptContent += `- Copia247's Messages: ${botMessageCount}\n\n`;
        transcriptContent += `## Transcript\n\n`;

        conversation.forEach((msg) => {
            if (msg.isSystem) {
                transcriptContent += `[SYSTEM] ${new Date(msg.timestamp).toLocaleTimeString()}: ${msg.text}\n\n`;
            } else {
                const msgTime = new Date(msg.timestamp).toLocaleTimeString();
                transcriptContent += `${msg.sender} (${msgTime}):\n${msg.text}\n\n`;
            }
        });

        const modal = document.createElement('div');
        modal.style.position = 'fixed';
        modal.style.top = '0';
        modal.style.left = '0';
        modal.style.width = '100%';
        modal.style.height = '100%';
        modal.style.backgroundColor = 'rgba(0, 0, 0, 0.5)';
        modal.style.display = 'flex';
        modal.style.justifyContent = 'center';
        modal.style.alignItems = 'center';
        modal.style.zIndex = '2000';

        const modalContent = document.createElement('div');
        modalContent.style.backgroundColor = 'white';
        modalContent.style.padding = '20px';
        modalContent.style.borderRadius = '10px';
        modalContent.style.width = '400px';
        modalContent.style.maxWidth = '90%';
        modalContent.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';

        modalContent.innerHTML = `
            <h4 style="margin-top: 0; color: #333; font-weight: 600;">Conversation Summary</h4>
            <p>This conversation contains ${messageCount} messages (${userMessageCount} from you and ${botMessageCount} from Copia247).</p>
            <p>Would you like to download the complete transcript?</p>
            <div style="display: flex; gap: 10px; justify-content: flex-end; margin-top: 20px;">
                <button id="cancel-transcript" style="padding: 8px 16px; border: 1px solid #ccc; background: #f8f9fa; border-radius: 4px; cursor: pointer;">Cancel</button>
                <button id="download-txt" style="padding: 8px 16px; border: none; background: #0c022f; color: white; border-radius: 4px; cursor: pointer;">Download as TXT</button>
            </div>
        `;

        modal.appendChild(modalContent);
        document.body.appendChild(modal);

        document.getElementById('download-txt').addEventListener('click', () => {
            downloadTranscript('txt', transcriptContent, `copia247-chat-${fileDate}-${fileTime}`);
            document.body.removeChild(modal);
        });

        document.getElementById('cancel-transcript').addEventListener('click', () => {
            document.body.removeChild(modal);
        });

        modal.addEventListener('click', (e) => {
            if (e.target === modal) {
                document.body.removeChild(modal);
            }
        });
    };

    const downloadTranscript = (fileType, content, filename) => {
        const element = document.createElement('a');

        if (fileType === 'txt') {
            content = content
                .replace(/# (.*)/g, '$1\n============================')
                .replace(/## (.*)/g, '$1\n----------------------------')
                .replace(/\*\*(.*)\*\*/g, '$1')
                .replace(/\*(.*)\*/g, '$1')
                .replace(/- /g, '• ');
        }

        const file = new Blob([content], { type: 'text/plain' });
        element.href = URL.createObjectURL(file);
        element.download = `${filename}.${fileType}`;
        document.body.appendChild(element);
        element.click();
        document.body.removeChild(element);

        setConversation(prev => [...prev, {
            sender: "System",
            text: `Transcript downloaded as ${filename}.${fileType}`,
            timestamp: new Date().toISOString(),
            isSystem: true,
            read: true
        }]);
    };

    return (
        <>
            <ToggleButton
                toggleChat={toggleChat}
                unreadMessages={unreadMessages}
                chatOpen={chatOpen}
                minimized={minimized}
            />

            {chatOpen && !minimized && (
                <ChatWindow
                    showWelcome={showWelcome}
                    isLoading={isLoading}
                    isStreaming={isStreaming}
                    mcpLoadingState={mcpLoadingState}
                    isAuthenticated={isAuthenticated}
                    authError={authError}
                    conversation={conversation}
                    formatTimestamp={formatTimestamp}
                    renderMarkdown={renderMarkdown}
                    suggestedResponses={suggestedResponses}
                    input={input}
                    email={email}
                    guid={guid}
                    fullscreen={fullscreen}
                    isMobile={isMobile}
                    toggleMinimize={toggleMinimize}
                    toggleFullscreen={toggleFullscreen}
                    clearChat={clearChat}
                    startChat={startChat}
                    handleSubmit={handleSubmit}
                    setInput={setInput}
                    handleSuggestedResponse={handleSuggestedResponse}
                    updateActivity={updateActivity}
                    markMessagesAsRead={markMessagesAsRead}
                    summarizeConversation={summariseConversation}
                    streamingMessageIndex={streamingMessageIndex}
                    isDownloading={isDownloading}
                />
            )}
        </>
    );
}

export default ChatBot;